import React, { useCallback, useEffect, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { Content } from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { InfoMessage } from 'src/core/POS/MessageWithIcon/InfoMessage';
import { BulkEditDeliveryTypeDialog } from 'src/dialogs/BulkEdits/BulkEditDeliveryTypeDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserCanManageSeatSaver } from 'src/hooks/useUserHasListingPermissions';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  DeliveryType,
  Event,
  Feature,
  ListingQuery,
  TicketTypeOverride,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_DELIVERY_TYPE_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchBulkUpdateDeliveryType } from './LaunchBulkUpdateDeliveryType';

export const useBulkUpdateDeliveryType = (
  filterQuery: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const hasManageSeatSaverPermission = useUserCanManageSeatSaver();

  const hasSeatSaverFeature = useUserHasFeature(Feature.SeatSaver);

  const updateKey = event
    ? `inventory-delivery-type-event-${event.virtId}`
    : INVENTORY_BULK_UPDATE_DELIVERY_TYPE_UPDATE_KEY;

  const { showErrorDialog } = useClientLogContext();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const {
    openDialog: openBulkEditDeliveryTypeDialog,
    closeDialog: closeBulkEditDeliveryTypeDialog,
  } = useDialog(DialogId.BulkEditDeliveryType, BulkEditDeliveryTypeDialog);
  const onSubmit = useCallback(
    async (
      deliveryType: DeliveryType | null,
      tickeTypePriorities: TicketTypeOverride | null,
      _updateSalesOfListings: boolean, // not used
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateDeliveryTypePreview(filterQuery);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateDeliveryTypePreview', error, {
              trackErrorData: { filterQuery },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (deliveryType && tickeTypePriorities) {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateDeliveryType(
              {
                item1: preview!.preview,
                item2: deliveryType,
                item3: tickeTypePriorities.overrides[0].ticketTypePriorities,
              },
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                refreshExpandedListItems();
              }

              closeBulkEditDeliveryTypeDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdateDeliveryType', error, {
              trackErrorData: {
                preview,
                deliveryType,
                tickeTypePriorities,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      closeBulkEditDeliveryTypeDialog,
      filterQuery,
      refreshExpandedListItems,
      setIsLoading,
      showErrorDialog,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkEditDeliveryTypeDialog({
      header: <Content id={ContentId.UpdateDeliveryTypeForPlaceholders} />,
      updateKey,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditDeliveryTypeDialog,
      entityType: ActionOutboxEntityType.Listing,
      infoMessage: (
        <InfoMessage
          message={<Content id={ContentId.BulkUpdatePlaceholdersMessage} />}
        />
      ),
      showUpdateAssociatedSalesOption: false, // Placeholder listings don't have allocated sales
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditDeliveryType,
        onSelect: openDialogStableRef,
      },
      disabled: !hasManageSeatSaverPermission || !hasSeatSaverFeature,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditDeliveryType,
        disabled: !hasManageSeatSaverPermission || !hasSeatSaverFeature,
      });
    };
  }, [
    hasManageSeatSaverPermission,
    hasSeatSaverFeature,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkEditDeliveryTypeDialog({
        header: <Content id={ContentId.UpdateDeliveryTypeForPlaceholders} />,
        updateKey,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkEditDeliveryTypeDialog,
        entityType: ActionOutboxEntityType.Listing,
        infoMessage: (
          <InfoMessage
            message={<Content id={ContentId.BulkUpdatePlaceholdersMessage} />}
          />
        ),
        showUpdateAssociatedSalesOption: false, // Placeholder listings don't have allocated sales
      });
    },
    [
      closeBulkEditDeliveryTypeDialog,
      onSubmit,
      openBulkEditDeliveryTypeDialog,
      setIsLoading,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasManageSeatSaverPermission || !hasSeatSaverFeature) {
      return [];
    }
    return [
      {
        title: ContentId.UpdateDeliveryTypeForPlaceholders,
        disabled: isLoading,
        onClick,
      },
    ];
  }, [hasManageSeatSaverPermission, hasSeatSaverFeature, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchBulkUpdateDeliveryType
        key="purchaseDeliveryTypeUpdate"
        onClick={onClick}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
